import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import { navigate } from 'raviger';
import _ from 'lodash';

import withAuthentication from '../../../hoc/withAuthentication';
import withLayout from '../../../hoc/withLayout';

import Permission from '../../../components/Permission/Permission';

import { useAlertStore } from '../../../store/providers/Alert';
import { useMarketplaceVendorStore } from '../../../store/providers/MarketplaceVendor';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../../store/actions/breadcrumb';

import Spinner from '../../../components/Spinner/Spinner';

import * as constants from '../../../constants';
import { columns, sorting } from './MarketplaceVendorsSchema';

const SideMenu = React.lazy(() => import('../../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../../components/PageHeader/PageHeader'));
const BaseList = React.lazy(() => import('../../../components/Lists/BaseList/BaseList'));

function MarketplaceVendors() {
    // Redux state
    const dispatch = useDispatch();

    // Store state
    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useMarketplaceVendorStore();

    // Local state
    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);

    const pagePermission = constants.PERMISSION_MARKETPLACE_VENDORS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (marketplaceVendor) => {
        navigate(`/marketplace/vendors/edit/${marketplaceVendor.id}`);
    };

    const addedHandler = () => {
        navigate(`/marketplace/vendors/new`);
    };

    const deletedHandler = (marketplaceVendor) => {
        deleteRecord({ marketplaceVendorId: marketplaceVendor.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }
        }

        setIsBusy(!_.isEmpty(data) && (data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list')));
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'marketplace', parent: 'Marketplace', text: 'Marketplace Vendors', path: window.location.pathname }));
        listRecords();

        setIsMounted(true);
    }, [hasPermission, isMounted, listRecords, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="marketplace" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Marketplace Vendors" />
                            <Panel>
                                <Panel.Body>
                                    <div className="row" style={{ paddingTop: '20px' }}>
                                        <div className="col-md-12">
                                            <BaseList
                                                compType="Vendor"
                                                listData={listData}
                                                sorting={sorting}
                                                columns={columns}
                                                isBusy={isBusy}
                                                activeValue="Active"
                                                activeField="statusName"
                                                hideCopy={true}
                                                hidePrint={true}
                                                permissionEdit={canEdit}
                                                added={() => addedHandler()}
                                                selected={(marketplaceVendor) => selectedHandler(marketplaceVendor)}
                                                deleted={(marketplaceVendor) => deletedHandler(marketplaceVendor)}
                                                refreshed={() => refreshedHandler()} />
                                        </div>
                                    </div>
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(MarketplaceVendors));
